// src/pages/ProductSales.jsx
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import { Card } from "../../components/card/card.jsx";

import {
  // US product sales
  fetchOrdersProductSales,
  fetchOrdersProductSalesByMonth,

  // Saleor product sales
  fetchChannelProductSales,
  fetchChannelProductSalesByMonth,

  // Product options for filter dropdown
  fetchAnalyticsProductOptions,

  // US product units
  fetchOrdersProductUnits,
  fetchOrdersProductUnitsByMonth,

  // Saleor product units
  fetchChannelProductUnits,
  fetchChannelProductUnitsByMonth,
} from "../../actions/orders_actions";

// Shaker Pro (shakerpro.barsys.com) sales are merged into the US channel
// responses by the backend — no separate channel here.

// ----- Helpers for formatting -----

const normalizeRangeKey = (val) => {
  const v = String(val || "").toLowerCase().trim();

  if (v === "7 days" || v === "7d") return "7d";
  if (v === "14 days" || v === "14d") return "14d";
  if (v === "month") return "month";

  if (v === "today") return "today";
  if (v === "yesterday") return "yesterday";
  if (v === "30d" || v === "last 30 days" || v === "30 days") return "30d";

  if (v === "this week" || v === "this_week" || v === "week") return "this_week";
  if (v === "this month" || v === "this_month") return "this_month";
  if (v === "this quarter" || v === "this_quarter" || v === "quarter") return "this_quarter";

  if (v === "ytd" || v === "year-to-date" || v === "year to date") return "ytd";

  return "7d";
};

const RANGE_CONFIG = {
  today: { label: "Today", prevLabel: "Yesterday" },
  yesterday: { label: "Yesterday", prevLabel: "Day before" },

  "7d": { label: "Last 7 Days", prevLabel: "Previous 7 Days" },
  "14d": { label: "Last 14 Days", prevLabel: "Previous 14 Days" },
  "30d": { label: "Last 30 Days", prevLabel: "Previous 30 Days" },

  this_week: { label: "This Week", prevLabel: "Last Week" },
  this_month: { label: "This Month", prevLabel: "Last Month" },
  month: { label: "Last Month (Rolling)", prevLabel: "Previous month" },
  this_quarter: { label: "This Quarter", prevLabel: "Last Quarter" },
  ytd: { label: "Year to Date", prevLabel: "Previous YTD" },
};

// options shown in the Daily Sales dropdown
const SALES_RANGE_OPTIONS_US = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "This Week", value: "this_week" },
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 14 Days", value: "14d" },
  { label: "Last 30 Days", value: "30d" },
  { label: "This Month", value: "this_month" },
  { label: "Last Month (rolling)", value: "month" },
  { label: "This Quarter", value: "this_quarter" },
  { label: "Year to Date", value: "ytd" },
];

// Saleor channels keep the legacy list
const SALES_RANGE_OPTIONS_CHANNEL = [
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 14 Days", value: "14d" },
  { label: "Last Month (rolling)", value: "month" },
];

// (keep your units dropdown unchanged)
const UNITS_RANGE_OPTIONS = [
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 14 Days", value: "14d" },
  { label: "Last Month", value: "month" },
];

const SALES_ALLOWED_US = new Set(SALES_RANGE_OPTIONS_US.map((o) => o.value));
const SALES_ALLOWED_CHANNEL = new Set(SALES_RANGE_OPTIONS_CHANNEL.map((o) => o.value));

const rangeOptionsUS = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "This Week", value: "this_week" },
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 14 Days", value: "14d" },
  { label: "Last 30 Days", value: "30d" },
  { label: "This Month", value: "this_month" },
  { label: "Last Month (Rolling)", value: "month" },
  { label: "This Quarter", value: "this_quarter" },
  { label: "Year to Date", value: "ytd" },
];

const rangeOptionsChannel = [
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 14 Days", value: "14d" },
  { label: "Last Month", value: "month" },
];

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(0)}%`;
};

const SPECIAL_THEME_HEX = "#798e98"; // the exact theme you mentioned
const SPECIAL_PRODUCT_COLORS_4 = ["#f8f9fa", "#ced4da", "#868e96", "#495057"]; // bigger contrast

const isSpecialTheme = (hex) => String(hex || "").toLowerCase() === SPECIAL_THEME_HEX;

// Always return N colors (cycle through the 4 if series > 4)
const fixed4Greys = (count) =>
  Array.from({ length: Math.max(1, Number(count) || 1) }, (_, i) => SPECIAL_PRODUCT_COLORS_4[i % 4]);

const shortDayLabelFromDate = (dateStr) => {
  if (!dateStr) return "";

  const base = String(dateStr).split(/[ T]/)[0];
  const parts = base.split("-");
  if (parts.length !== 3) return String(dateStr);

  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);

  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return String(dateStr);

  const dt = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  }).format(dt);
};

const monthLabelFromNumber = (year, month) => {
  if (!month) return "";
  const y = year || new Date().getFullYear();
  const dt = new Date(Date.UTC(y, month - 1, 1));
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    timeZone: "UTC",
  }).format(dt);
};

const normalizeSeriesPoint = (p, type = "day") => {
  if (!p || typeof p !== "object") return null;

  const revenue = safeNumber(p.revenue);
  let label = p.label;

  if (!label) {
    if (type === "day" && p.date) {
      label = shortDayLabelFromDate(p.date);
    } else if (type === "month") {
      if (p.month) label = monthLabelFromNumber(p.year, p.month);
      else if (p.date) {
        label = monthLabelFromNumber(
          Number(String(p.date).substring(0, 4)),
          Number(String(p.date).substring(5, 7))
        );
      }
    }
  }

  return { label: label || "", revenue, raw: p };
};

// Normalize units point
const normalizeUnitsPoint = (p, type = "day") => {
  if (!p || typeof p !== "object") return null;

  const qty = safeNumber(p.totalUnits ?? p.qty ?? p.units ?? p.quantity ?? p.total ?? 0);
  const amount = safeNumber(p.amount ?? p.revenue ?? p.totalRevenue ?? p.sales ?? 0);

  let label = p.label;

  if (!label) {
    if (type === "day" && p.date) label = shortDayLabelFromDate(p.date);
    else if (type === "month") {
      if (p.month) label = monthLabelFromNumber(p.year, p.month);
      else if (p.date) {
        label = monthLabelFromNumber(
          Number(String(p.date).substring(0, 4)),
          Number(String(p.date).substring(5, 7))
        );
      }
    }
  }

  const products = Array.isArray(p.products) ? p.products : [];
  const normProducts = products.map((pp) => ({
    ...pp,
    productName: pp?.productName ?? pp?.name,
    qty: safeNumber(pp?.qty ?? pp?.quantity ?? pp?.units ?? pp?.totalUnits ?? 0),
    amount: safeNumber(pp?.amount ?? pp?.revenue ?? pp?.totalRevenue ?? pp?.sales ?? 0),
  }));

  return { label: label || "", qty, amount, products: normProducts, raw: p };
};

// Build per-product SERIES
const buildPerProductMetricSeries = (buckets, metric = "qty") => {
  const list = Array.isArray(buckets) ? buckets : [];

  const productNames = new Set();
  list.forEach((b) => {
    (b.products || []).forEach((pp) => {
      const name = pp?.productName ?? pp?.name;
      if (name) productNames.add(String(name));
    });
  });

  const names = Array.from(productNames).sort((a, b) => a.localeCompare(b));

  const series = names.map((name) => {
    const data = list.map((b) => {
      const found = (b.products || []).find((pp) => String(pp?.productName ?? pp?.name) === name);
      if (!found) return 0;
      return safeNumber(metric === "amount" ? found.amount : found.qty);
    });
    return { name, data };
  });

  return series;
};

const lighten = (hex, amt) => {
  const h = String(hex || "").replace(/^#/, "");
  const num = parseInt(h || "000000", 16);

  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.round(r + (255 - r) * amt);
  g = Math.round(g + (255 - g) * amt);
  b = Math.round(b + (255 - b) * amt);

  const n = ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
  return `#${n}`;
};

const darken = (hex, amt) => {
  const h = String(hex || "").replace(/^#/, "");
  const num = parseInt(h || "000000", 16);

  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  const a = clamp01(amt);

  r = Math.round(r * (1 - a));
  g = Math.round(g * (1 - a));
  b = Math.round(b * (1 - a));

  const n = ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
  return `#${n}`;
};


const clamp01 = (n) => Math.max(0, Math.min(1, n));

const makeThemePalette = (baseHex, count) => {
  const n = Math.max(1, Number(count) || 1);
  if (n === 1) return [baseHex];

  // Wider range + alternate extremes so adjacent bars differ a lot,
  // while staying in the SAME hue family.
  const min = -0.22; // darken 22%
  const max = 0.72;  // lighten 72%

  const steps = Array.from({ length: n }, (_, i) => {
    const t = i / (n - 1);
    return min + (max - min) * t; // -0.22 ... +0.72
  });

  // Reorder: darkest, lightest, 2nd darkest, 2nd lightest, ...
  const reordered = [];
  let l = 0;
  let r = steps.length - 1;
  while (l <= r) {
    if (l === r) {
      reordered.push(steps[l]);
      break;
    }
    reordered.push(steps[l]);
    reordered.push(steps[r]);
    l++;
    r--;
  }

  return reordered.map((delta) => (delta >= 0 ? lighten(baseHex, clamp01(delta)) : darken(baseHex, clamp01(-delta))));
};

const normalizeCssColorToHex = (input, fallback = "#0d6efd") => {
  const v = String(input || "").trim();
  if (!v) return fallback;

  // Resolve var(--foo)
  if (/^var\(/i.test(v) && typeof document !== "undefined") {
    const m = v.match(/^var\(\s*(--[^,\s)]+)/i);
    if (m?.[1]) {
      const resolved = getComputedStyle(document.body).getPropertyValue(m[1]).trim();
      return normalizeCssColorToHex(resolved, fallback);
    }
  }

  // #RGB or #RRGGBB
  if (v.startsWith("#")) {
    const hex = v.slice(1);
    if (/^[0-9a-f]{3}$/i.test(hex)) {
      return `#${hex
        .split("")
        .map((c) => c + c)
        .join("")}`.toLowerCase();
    }
    if (/^[0-9a-f]{6}$/i.test(hex)) return `#${hex}`.toLowerCase();
    return fallback;
  }

  // rgb(...) / rgba(...)
  const rgb = v.match(/rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/i);
  if (rgb) {
    const r = Math.max(0, Math.min(255, Math.round(Number(rgb[1]))));
    const g = Math.max(0, Math.min(255, Math.round(Number(rgb[2]))));
    const b = Math.max(0, Math.min(255, Math.round(Number(rgb[3]))));
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  }

  // "13, 110, 253" (Bootstrap *-rgb variables)
  const triplet = v.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/);
  if (triplet) {
    const r = Math.max(0, Math.min(255, Number(triplet[1])));
    const g = Math.max(0, Math.min(255, Number(triplet[2])));
    const b = Math.max(0, Math.min(255, Number(triplet[3])));
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  }

  return fallback;
};

const getThemeColorFromCss = () =>
  normalizeCssColorToHex(
    typeof document !== "undefined"
      ? getComputedStyle(document.body).getPropertyValue("--bs-theme").trim()
      : "",
    "#0d6efd"
  );

const parseCssColorToRgb = (input) => {
  const s = String(input || "").trim().toLowerCase();
  const fallback = { r: 13, g: 110, b: 253 }; // #0d6efd

  if (!s) return fallback;

  // rgb() / rgba()
  if (s.startsWith("rgb")) {
    const m = s.match(/rgba?\(([^)]+)\)/);
    if (!m) return fallback;
    const parts = m[1].split(",").map((p) => p.trim());
    const r = Number(parts[0]);
    const g = Number(parts[1]);
    const b = Number(parts[2]);
    if ([r, g, b].every((x) => Number.isFinite(x))) {
      return {
        r: Math.max(0, Math.min(255, r)),
        g: Math.max(0, Math.min(255, g)),
        b: Math.max(0, Math.min(255, b)),
      };
    }
    return fallback;
  }

  // hex
  let hex = s.replace(/^#/, "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join(""); // #RGB -> #RRGGBB
  if (hex.length === 8) hex = hex.slice(0, 6); // ignore alpha
  if (!/^[0-9a-f]{6}$/.test(hex)) return fallback;

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return { r, g, b };
};

const rgbToHsl = (r255, g255, b255) => {
  let r = r255 / 255;
  let g = g255 / 255;
  let b = b255 / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  const l = (max + min) / 2;
  let s = 0;

  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      default:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: clamp01(h), s: clamp01(s), l: clamp01(l) };
};

const hexToHsl = (hex) => {
  const { r, g, b } = parseCssColorToRgb(hex);
  return rgbToHsl(r, g, b);
};

const hslToHex = ({ h, s, l }) => {
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  h = clamp01(h);
  s = clamp01(s);
  l = clamp01(l);

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// theme-based series palette (distinct colors for each product)
const makeSeriesPalette = (baseHex, count) => {
  const n = Math.max(1, Number(count) || 1);
  const { h, s, l } = hexToHsl(baseHex);

  if (n === 1) return [baseHex];

  // Avoid very dark shades that look black on dark UI.
  const minL = Math.max(0.42, l - 0.1);
  const maxL = Math.min(0.88, l + 0.34);

  const sat = Math.min(0.9, s + 0.25);

  const lo = Math.min(minL, maxL);
  const hi = Math.max(minL, maxL);

  return Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 0 : i / (n - 1);
    return hslToHex({ h, s: sat, l: lo + (hi - lo) * t });
  });
};

const makeGreys = (count, { maxL, minL }) => {
  const n = Math.max(1, Number(count) || 1);
  const hi = typeof maxL === "number" ? maxL : 0.92;
  const lo = typeof minL === "number" ? minL : 0.46;

  return Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 0 : i / (n - 1);
    const l = hi + (lo - hi) * t;
    return hslToHex({ h: 0, s: 0, l });
  });
};

// Neutral (grey/white/black) theme colors should use greys.
// IMPORTANT: in dark UI, never go near-black.
const makeProductPalette = (baseHex, count, isDarkUi = false) => {
  const { s } = hexToHsl(baseHex);
  const neutral = s < 0.22;

  if (neutral) {
    return isDarkUi
      ? makeGreys(count, { maxL: 0.92, minL: 0.62 })
      : makeGreys(count, { maxL: 0.75, minL: 0.28 });
  }

  return makeSeriesPalette(baseHex, count);
};

// Chart type helper (bar | line)
const normalizeChartType = (v) => (String(v || "").toLowerCase() === "line" ? "line" : "bar");

const NO_STATE_FILTERS = {
  normal: { filter: { type: "none", value: 0 } },
  hover: { filter: { type: "none", value: 0 } },
  active: { filter: { type: "none", value: 0 } },
  inactive: { filter: { type: "none", value: 0 }, opacity: 1 },
};

// --- Dark UI detection (cover/theme mode) ---
const isTransparentCssColor = (c) => {
  const s = String(c || "").trim().toLowerCase();
  if (!s) return true;
  if (s === "transparent") return true;
  const m = s.match(/rgba\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)/);
  if (m) {
    const a = Number(m[1]);
    return Number.isFinite(a) && a === 0;
  }
  return false;
};

const getEffectiveBackgroundColor = () => {
  if (typeof document === "undefined") return "rgb(255,255,255)";

  const candidates = [
    document.body,
    document.getElementById("root"),
    document.documentElement,
  ].filter(Boolean);

  for (const start of candidates) {
    let el = start;
    while (el) {
      const bg = getComputedStyle(el).backgroundColor;
      if (bg && !isTransparentCssColor(bg)) return bg;
      el = el.parentElement;
    }
  }

  return getComputedStyle(document.body).backgroundColor || "rgb(255,255,255)";
};

const getIsDarkUiFromDom = () => {
  if (typeof document === "undefined") return false;

  const bg = getEffectiveBackgroundColor();
  const { r, g, b } = parseCssColorToRgb(bg);
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  return lum < 0.5;
};

const makeLegendToggleEvents = () => ({
  legendClick: function (chartContext, seriesIndex, config) {
    const chart = chartContext; // <-- THIS is the Apex chart instance
    if (!chart) return false;

    const names = chart?.w?.globals?.seriesNames || config?.globals?.seriesNames || [];
    const name = names[seriesIndex];
    if (!name) return false;

    // simplest + most reliable
    if (typeof chart.toggleSeries === "function") {
      chart.toggleSeries(name);
      return false;
    }

    // fallback
    const collapsed = chart?.w?.globals?.collapsedSeriesIndices || [];
    const isHidden = collapsed.includes(seriesIndex);

    if (isHidden) chart.showSeries?.(name);
    else chart.hideSeries?.(name);

    return false; // prevent default
  },
});

const makeLegendIsolateEvents = () => ({
  legendClick: function (chartContext, seriesIndex, config) {
    const chart = chartContext;
    if (!chart) return false;

    const names = chart?.w?.globals?.seriesNames || config?.globals?.seriesNames || [];
    if (!names.length) return false;

    const collapsed = chart?.w?.globals?.collapsedSeriesIndices || [];
    const isolated = collapsed.length === names.length - 1 && !collapsed.includes(seriesIndex);

    if (isolated) {
      names.forEach((n) => chart.showSeries?.(n));
    } else {
      names.forEach((n, i) => (i === seriesIndex ? chart.showSeries?.(n) : chart.hideSeries?.(n)));
    }
    return false;
  },
});


// Force legend/series/tooltip markers to match colors, and respect dark/light UI.
// FIX: if you pass a custom chart.events.legendClick handler, disable Apex default legend toggle
//      (otherwise it can conflict or do nothing depending on version/config).
const applyLegendFixes = (opts, seriesColors, isDarkUi, events) => {
  const legend = opts.legend || {};
  const legendMarkers = legend.markers || {};
  const tooltip = opts.tooltip || {};
  const tooltipMarker = tooltip.marker || {};

  const mergedEvents = { ...(opts.chart?.events || {}), ...(events || {}) };
  const hasCustomLegendClick = typeof mergedEvents.legendClick === "function";

  const legendTextColor = isDarkUi ? "#e9ecef" : "#212529";
  const legendTextColors = Array.from({ length: Math.max(1, seriesColors?.length || 1) }, () => legendTextColor);

  return {
    ...opts,
    chart: {
      ...(opts.chart || {}),
      events: mergedEvents,
    },
    theme: {
      ...(opts.theme || {}),
      mode: isDarkUi ? "dark" : "light",
      monochrome: { enabled: false },
    },
    states: NO_STATE_FILTERS,

    colors: seriesColors,
    stroke: { ...(opts.stroke || {}), colors: seriesColors, opacity: 1 },
    fill: { ...(opts.fill || {}), colors: seriesColors, opacity: 1 },

    tooltip: {
      ...tooltip,
      marker: { ...tooltipMarker, fillColors: seriesColors },
    },

    legend: {
      ...legend,
      onItemClick: { ...(legend.onItemClick || {}), toggleDataSeries: !hasCustomLegendClick },
      onItemHover: { ...(legend.onItemHover || {}), highlightDataSeries: true },

      //  legend TEXT stays fixed (does NOT follow series colors)
      labels: {
        ...(legend.labels || {}),
        useSeriesColors: false,
        colors: legendTextColors,
      },

      //  legend DOTS stay series colors
      markers: {
        ...legendMarkers,
        fillColors: seriesColors,
        strokeColor: "transparent",
      },
    },
  };
};

const applyLineFixes = (opts, seriesColors, isDarkUi, events) => {
  const withLegend = applyLegendFixes(opts, seriesColors, isDarkUi, events);

  return {
    ...withLegend,
    stroke: {
      ...(withLegend.stroke || {}),
      curve: "smooth",
      width: 3,
      colors: seriesColors,
      opacity: 1,
    },
    markers: {
      ...(withLegend.markers || {}),
      size: 3,
      strokeWidth: 2,
      colors: seriesColors,
      strokeColors: seriesColors,
    },
  };
};

// ----- Page -----

const ProductSales = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // --- Channel logic ---
  const normalizeChannel = (v) => {
    if (!v) return "US";
    switch (String(v).toLowerCase()) {
      case "us":
        return "US";
      case "uk":
        return "UK";
      case "india":
        return "India";
      case "shakerpro":
        return "US"; // shakerpro is merged into the US channel
      default:
        return "US";
    }
  };

  const hydratedRef = useRef(false);
  const monthlyUnitsCardRef = useRef(null);
  const pendingScrollToMonthlyUnitsRef = useRef(false);

  // Deduping fetches
  const lastDailyKeyRef = useRef("");
  const lastMonthlyKeyRef = useRef("");
  const lastDailyUnitsKeyRef = useRef("");
  const lastMonthlyUnitsKeyRef = useRef("");

  // URL-driven initial state
  const [channel, setChannel] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("channel");
    return normalizeChannel(p);
  });

  const [salesRange, setSalesRange] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("salesRange");
    return normalizeRangeKey(p || "7d");
  });

  const [unitsRange, setUnitsRange] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("unitsRange");
    return normalizeRangeKey(p || "7d");
  });

  const [dailySalesChartType, setDailySalesChartType] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("dailySalesChartType");
    return normalizeChartType(p || "bar");
  });

  const [monthlySalesChartType, setMonthlySalesChartType] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("monthlySalesChartType");
    return normalizeChartType(p || "bar");
  });

  const [dailyUnitsChartType, setDailyUnitsChartType] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("dailyUnitsChartType");
    return normalizeChartType(p || "bar");
  });

  const [monthlyUnitsChartType, setMonthlyUnitsChartType] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("monthlyUnitsChartType");
    return normalizeChartType(p || "bar");
  });

  const currentYear = new Date().getFullYear();

  const [salesYear, setSalesYear] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("salesYear");
    const y = Number(p);
    return Number.isFinite(y) && y > 2000 ? y : currentYear;
  });

  const [unitsYear, setUnitsYear] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("unitsYear");
    const y = Number(p);
    return Number.isFinite(y) && y > 2000 ? y : currentYear;
  });

  // independent metric toggles (kept, even if UI toggle removed)
  const [dailyUnitsMetric, setDailyUnitsMetric] = useState("qty");
  const [monthlyUnitsMetric, setMonthlyUnitsMetric] = useState("qty");

  // Theme (accent) + UI cover detection
  const [themeColor, setThemeColor] = useState(getThemeColorFromCss);
  const [isDarkUi, setIsDarkUi] = useState(() => getIsDarkUiFromDom());

  useEffect(() => {
    const update = () => {
      setThemeColor(getThemeColorFromCss());
      setIsDarkUi(getIsDarkUiFromDom());
    };

    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "style"] });

    return () => obs.disconnect();
  }, []);

  const secondColor = useMemo(() => lighten(themeColor, 0.4), [themeColor]);

  // -------- Product filter --------
  const { analytics_product_options, loading_analytics_product_options, analytics_product_options_error } = useSelector(
    (state) => ({
      analytics_product_options: state.orders?.analytics_product_options || [],
      loading_analytics_product_options: state.orders?.loading_analytics_product_options,
      analytics_product_options_error: state.orders?.analytics_product_options_error,
    })
  );

  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [appliedProductIds, setAppliedProductIds] = useState([]);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchAnalyticsProductOptions());
  }, [dispatch]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.("#productsMultiSelectDropdown")) {
        setProductsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // avoid loops
  useEffect(() => {
    const allowed = channel === "US" ? SALES_ALLOWED_US : SALES_ALLOWED_CHANNEL;
    const normalized = normalizeRangeKey(salesRange);

    if (!allowed.has(normalized)) {
      setSalesRange("7d");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]); // intentionally NOT depending on salesRange

  const toggleProductId = useCallback((id) => {
    const sid = String(id);
    setSelectedProductIds((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
  }, []);

  const clearSelectedProducts = useCallback(() => {
    setSelectedProductIds([]);
  }, []);

  const normalizeIdsKey = useCallback(
    (ids) =>
      (Array.isArray(ids) ? ids : [])
        .map((x) => String(x).trim())
        .filter(Boolean)
        .sort()
        .join(","),
    []
  );

  const selectedProductsLabel = useMemo(() => {
    if (!selectedProductIds.length) return "All products";
    const labelById = new Map((analytics_product_options || []).map((o) => [String(o.value), o.label]));
    const labels = selectedProductIds.map((id) => labelById.get(String(id)) || id);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
  }, [selectedProductIds, analytics_product_options]);

  const hasSelectionChanges = useMemo(() => {
    return normalizeIdsKey(selectedProductIds) !== normalizeIdsKey(appliedProductIds);
  }, [selectedProductIds, appliedProductIds, normalizeIdsKey]);

  const handleProductsQueryClick = useCallback(() => {
    setAppliedProductIds(selectedProductIds);

    lastDailyKeyRef.current = "";
    lastMonthlyKeyRef.current = "";
    lastDailyUnitsKeyRef.current = "";
    lastMonthlyUnitsKeyRef.current = "";

    setProductsDropdownOpen(false);
  }, [selectedProductIds]);

  // Restore filter from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const ch = normalizeChannel(params.get("channel") || channel);
    if (ch !== channel) setChannel(ch);

    const sr = normalizeRangeKey(params.get("salesRange") || salesRange);
    if (sr !== salesRange) setSalesRange(sr);

    const ur = normalizeRangeKey(params.get("unitsRange") || unitsRange);
    if (ur !== unitsRange) setUnitsRange(ur);

    const syRaw = Number(params.get("salesYear"));
    const sy = Number.isFinite(syRaw) && syRaw > 2000 ? syRaw : salesYear;
    if (sy !== salesYear) setSalesYear(sy);

    const uyRaw = Number(params.get("unitsYear"));
    const uy = Number.isFinite(uyRaw) && uyRaw > 2000 ? uyRaw : unitsYear;
    if (uy !== unitsYear) setUnitsYear(uy);

    const dum = params.get("dailyUnitsMetric");
    if (dum === "qty" || dum === "amount") setDailyUnitsMetric(dum);

    const mum = params.get("monthlyUnitsMetric");
    if (mum === "qty" || mum === "amount") setMonthlyUnitsMetric(mum);

    const productsCsv = params.get("products") || "";
    const productsFromUrl = productsCsv
      .split(",")
      .map((x) => String(x).trim())
      .filter(Boolean);

    if (productsFromUrl.length) {
      setSelectedProductIds(productsFromUrl);
      setAppliedProductIds(productsFromUrl);
      lastDailyKeyRef.current = "";
      lastMonthlyKeyRef.current = "";
      lastDailyUnitsKeyRef.current = "";
      lastMonthlyUnitsKeyRef.current = "";
    }

    const ct = normalizeChartType(params.get("dailySalesChartType") || dailySalesChartType);
    if (ct !== dailySalesChartType) setDailySalesChartType(ct);

    const mct = normalizeChartType(params.get("monthlySalesChartType") || monthlySalesChartType);
    if (mct !== monthlySalesChartType) setMonthlySalesChartType(mct);

    const duct = normalizeChartType(params.get("dailyUnitsChartType") || dailyUnitsChartType);
    if (duct !== dailyUnitsChartType) setDailyUnitsChartType(duct);

    const muct = normalizeChartType(params.get("monthlyUnitsChartType") || monthlyUnitsChartType);
    if (muct !== monthlyUnitsChartType) setMonthlyUnitsChartType(muct);

    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-hydrate on navigation
  useEffect(() => {
    if (!hydratedRef.current) return;

    const params = new URLSearchParams(location.search);

    const nextChannel = normalizeChannel(params.get("channel") || "US");
    const nextSalesRange = normalizeRangeKey(params.get("salesRange") || "7d");
    const nextUnitsRange = normalizeRangeKey(params.get("unitsRange") || "7d");

    const nextMonthlySalesChartType = normalizeChartType(params.get("monthlySalesChartType") || "bar");
    const nextDailyUnitsChartType = normalizeChartType(params.get("dailyUnitsChartType") || "bar");
    const nextMonthlyUnitsChartType = normalizeChartType(params.get("monthlyUnitsChartType") || "bar");

    const syRaw = Number(params.get("salesYear"));
    const nextSalesYear = Number.isFinite(syRaw) && syRaw > 2000 ? syRaw : currentYear;

    const uyRaw = Number(params.get("unitsYear"));
    const nextUnitsYear = Number.isFinite(uyRaw) && uyRaw > 2000 ? uyRaw : currentYear;

    const nextDum = params.get("dailyUnitsMetric");
    const nextDailyUnitsMetric = nextDum === "amount" ? "amount" : "qty";

    const nextMum = params.get("monthlyUnitsMetric");
    const nextMonthlyUnitsMetric = nextMum === "amount" ? "amount" : "qty";

    const productsCsv = params.get("products") || "";
    const nextProducts = productsCsv
      .split(",")
      .map((x) => String(x).trim())
      .filter(Boolean);

    const channelChanged = nextChannel !== channel;
    const salesRangeChanged = nextSalesRange !== salesRange;
    const unitsRangeChanged = nextUnitsRange !== unitsRange;
    const salesYearChanged = nextSalesYear !== salesYear;
    const unitsYearChanged = nextUnitsYear !== unitsYear;

    const currentProductsKey = normalizeIdsKey(appliedProductIds);
    const nextProductsKey = normalizeIdsKey(nextProducts);
    const productsChanged = nextProductsKey !== currentProductsKey;

    const dailyMetricChanged = nextDailyUnitsMetric !== dailyUnitsMetric;
    const monthlyMetricChanged = nextMonthlyUnitsMetric !== monthlyUnitsMetric;

    if (channelChanged) setChannel(nextChannel);
    if (salesRangeChanged) setSalesRange(nextSalesRange);
    if (unitsRangeChanged) setUnitsRange(nextUnitsRange);
    if (salesYearChanged) setSalesYear(nextSalesYear);
    if (unitsYearChanged) setUnitsYear(nextUnitsYear);

    if (dailyMetricChanged) setDailyUnitsMetric(nextDailyUnitsMetric);
    if (monthlyMetricChanged) setMonthlyUnitsMetric(nextMonthlyUnitsMetric);

    if (nextMonthlySalesChartType !== monthlySalesChartType) setMonthlySalesChartType(nextMonthlySalesChartType);
    if (nextDailyUnitsChartType !== dailyUnitsChartType) setDailyUnitsChartType(nextDailyUnitsChartType);
    if (nextMonthlyUnitsChartType !== monthlyUnitsChartType) setMonthlyUnitsChartType(nextMonthlyUnitsChartType);

    if (productsChanged) {
      setSelectedProductIds(nextProducts);
      setAppliedProductIds(nextProducts);
    }

    const nextDailySalesChartType = normalizeChartType(params.get("dailySalesChartType") || "bar");
    if (nextDailySalesChartType !== dailySalesChartType) setDailySalesChartType(nextDailySalesChartType);

    if (channelChanged || salesRangeChanged || productsChanged) lastDailyKeyRef.current = "";
    if (channelChanged || salesYearChanged || productsChanged) lastMonthlyKeyRef.current = "";
    if (channelChanged || unitsRangeChanged || productsChanged) lastDailyUnitsKeyRef.current = "";
    if (channelChanged || unitsYearChanged || productsChanged) lastMonthlyUnitsKeyRef.current = "";

    if (channelChanged || productsChanged) setProductsDropdownOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ----- Saleor vs US -----
  const isChannel = channel !== "US";
  const channelParam = useMemo(() => {
    return channel === "India" ? "india" : channel === "UK" ? "uk" : null;
  }, [channel]);

  // Currency symbol based on channel
  const currencySymbol = useMemo(() => {
    if (channel === "UK") return "£";
    if (channel === "India") return "₹";
    return "$";
  }, [channel]);

  const withDotThousands = (digits) => String(digits).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const formatCurrencyCompact = useCallback(
    (value) => {
      const n = safeNumber(value);
      const isNeg = n < 0;
      const intAbs = Math.trunc(Math.abs(n));
      return `${isNeg ? "-" : ""}${currencySymbol}${withDotThousands(intAbs)}`;
    },
    [currencySymbol]
  );

  const formatNumberAbbrev = (value) => {
    if (value === null || value === undefined || value === "") return "0";

    const s = String(value).trim();
    const isNeg = s.startsWith("-");
    const raw = isNeg ? s.slice(1) : s;

    const [intPartRaw, fracPart] = raw.split(".");
    const intDigits = (intPartRaw || "0").replace(/[^\d]/g, "") || "0";

    const formattedInt = withDotThousands(intDigits);
    return `${isNeg ? "-" : ""}${formattedInt}${fracPart !== undefined ? "." + fracPart : ""}`;
  };

  // Apex remount key (IMPORTANT: includes isDarkUi so cover changes rebuild charts)
  const chartKey = useMemo(
    () =>
      `${isDarkUi}-${themeColor}-${channel}-${salesRange}-${unitsRange}-${salesYear}-${unitsYear}-${normalizeIdsKey(
        appliedProductIds
      )}`,
    [isDarkUi, themeColor, channel, salesRange, unitsRange, salesYear, unitsYear, appliedProductIds, normalizeIdsKey]
  );

  // productIds => full option objects
  const selectedProductObjects = useMemo(() => {
    const byId = new Map((analytics_product_options || []).map((o) => [String(o.value), o]));
    return (appliedProductIds || []).map((id) => byId.get(String(id))).filter(Boolean);
  }, [appliedProductIds, analytics_product_options]);

  // US: flatten real UPCs
  const appliedUpcsForUS = useMemo(() => {
    const upcs = selectedProductObjects.flatMap((o) => (Array.isArray(o.upcs) ? o.upcs : []));
    return Array.from(new Set(upcs.map(String).map((s) => s.trim()).filter(Boolean)));
  }, [selectedProductObjects]);

  // India/UK: flatten real SKUs
  const appliedSkusForChannel = useMemo(() => {
    const skus = selectedProductObjects.flatMap((o) => (Array.isArray(o.skus) ? o.skus : []));
    return Array.from(new Set(skus.map(String).map((s) => s.trim()).filter(Boolean)));
  }, [selectedProductObjects]);

  // ----- Redux selectors -----
  const ordersState = useSelector((state) => state.orders || {});

  const {
    // US product sales
    orders_product_sales,
    loading_orders_product_sales,
    orders_product_sales_error,

    orders_product_sales_by_month,
    loading_orders_product_sales_by_month,
    orders_product_sales_by_month_error,

    // Saleor product sales
    channel_product_sales,
    loading_channel_product_sales,
    channel_product_sales_error,

    channel_product_sales_by_month,
    loading_channel_product_sales_by_month,
    channel_product_sales_by_month_error,

    // US product units
    orders_product_units,
    loading_orders_product_units,
    orders_product_units_error,

    orders_product_units_by_month,
    loading_orders_product_units_by_month,
    orders_product_units_by_month_error,

    // Saleor channel product units
    channel_product_units,
    loading_channel_product_units,
    channel_product_units_error,

    channel_product_units_by_month,
    loading_channel_product_units_by_month,
    channel_product_units_by_month_error,
  } = ordersState;

  // Bind UI to selected channel (sales)
  const dailyLoading = isChannel ? loading_channel_product_sales : loading_orders_product_sales;
  const dailyError = isChannel ? channel_product_sales_error : orders_product_sales_error;
  const dailyRootBound = isChannel ? channel_product_sales : orders_product_sales;

  const monthlyLoading = isChannel
    ? loading_channel_product_sales_by_month
    : loading_orders_product_sales_by_month;
  const monthlyError = isChannel ? channel_product_sales_by_month_error : orders_product_sales_by_month_error;
  const monthlyRootBound = isChannel ? channel_product_sales_by_month : orders_product_sales_by_month;

  // Bind UI to selected channel (units)
  const dailyUnitsLoading = isChannel ? loading_channel_product_units : loading_orders_product_units;
  const dailyUnitsError = isChannel ? channel_product_units_error : orders_product_units_error;
  const dailyUnitsRootBound = isChannel ? channel_product_units : orders_product_units;

  const monthlyUnitsLoading = isChannel
    ? loading_channel_product_units_by_month
    : loading_orders_product_units_by_month;
  const monthlyUnitsError = isChannel
    ? channel_product_units_by_month_error
    : orders_product_units_by_month_error;
  const monthlyUnitsRootBound = isChannel ? channel_product_units_by_month : orders_product_units_by_month;

  // ----- Range labels -----
  const canonicalSalesRangeKey = normalizeRangeKey((dailyRootBound && dailyRootBound.range) || salesRange);
  const salesRangeCfg = RANGE_CONFIG[canonicalSalesRangeKey] || RANGE_CONFIG["7d"];

  const canonicalUnitsRangeKey = normalizeRangeKey((dailyUnitsRootBound && dailyUnitsRootBound.range) || unitsRange);
  const unitsRangeCfg = RANGE_CONFIG[canonicalUnitsRangeKey] || RANGE_CONFIG["7d"];

  // URL sync without string-fight loops
  useEffect(() => {
    if (!hydratedRef.current) return;

    const desiredParams = new URLSearchParams();
    desiredParams.set("channel", channel);
    desiredParams.set("salesRange", salesRange);
    desiredParams.set("unitsRange", unitsRange);
    desiredParams.set("salesYear", String(salesYear));
    desiredParams.set("unitsYear", String(unitsYear));
    desiredParams.set("dailyUnitsMetric", dailyUnitsMetric);
    desiredParams.set("monthlyUnitsMetric", monthlyUnitsMetric);
    desiredParams.set("dailySalesChartType", dailySalesChartType);
    desiredParams.set("monthlySalesChartType", monthlySalesChartType);
    desiredParams.set("dailyUnitsChartType", dailyUnitsChartType);
    desiredParams.set("monthlyUnitsChartType", monthlyUnitsChartType);

    if (appliedProductIds?.length) desiredParams.set("products", appliedProductIds.map(String).join(","));

    const currentParams = new URLSearchParams(location.search);
    const managedKeys = [
      "channel",
      "salesRange",
      "unitsRange",
      "salesYear",
      "unitsYear",
      "dailyUnitsMetric",
      "monthlyUnitsMetric",
      "dailySalesChartType",
      "monthlySalesChartType",
      "dailyUnitsChartType",
      "monthlyUnitsChartType",
      "products",
    ];

    let needsUpdate = false;
    for (const k of managedKeys) {
      const desired = desiredParams.get(k);
      const current = currentParams.get(k);
      if (desired == null) {
        if (current != null) {
          needsUpdate = true;
          break;
        }
      } else if (current !== desired) {
        needsUpdate = true;
        break;
      }
    }

    if (needsUpdate) {
      navigate(
        { pathname: "/orders/product-sales", search: `?${desiredParams.toString()}` },
        { replace: true, preventScrollReset: true }
      );
    }
  }, [
    channel,
    salesRange,
    unitsRange,
    salesYear,
    unitsYear,
    appliedProductIds,
    dailyUnitsMetric,
    monthlyUnitsMetric,
    dailySalesChartType,
    monthlySalesChartType,
    dailyUnitsChartType,
    monthlyUnitsChartType,
    navigate,
    location.search,
  ]);

  // ----- Effects: fetch Sales daily + monthly -----
  useEffect(() => {
    const key = `${channel}|sales|${salesRange}|${normalizeIdsKey(appliedProductIds)}`;
    if (lastDailyKeyRef.current === key) return;
    lastDailyKeyRef.current = key;

    if (isChannel) {
      if (!channelParam) return;
      dispatch(fetchChannelProductSales(channelParam, salesRange, appliedSkusForChannel));
    } else {
      dispatch(fetchOrdersProductSales(salesRange, appliedUpcsForUS));
    }
  }, [
    dispatch,
    channel,
    isChannel,
    channelParam,
    salesRange,
    appliedProductIds,
    normalizeIdsKey,
    appliedSkusForChannel,
    appliedUpcsForUS,
  ]);

  useEffect(() => {
    const key = `${channel}|sales|${salesYear}|${normalizeIdsKey(appliedProductIds)}`;
    if (lastMonthlyKeyRef.current === key) return;
    lastMonthlyKeyRef.current = key;

    if (!salesYear) return;

    if (isChannel) {
      if (!channelParam) return;
      dispatch(fetchChannelProductSalesByMonth(channelParam, salesYear, appliedSkusForChannel));
    } else {
      dispatch(fetchOrdersProductSalesByMonth(salesYear, appliedUpcsForUS));
    }
  }, [
    dispatch,
    channel,
    isChannel,
    channelParam,
    salesYear,
    appliedProductIds,
    normalizeIdsKey,
    appliedSkusForChannel,
    appliedUpcsForUS,
  ]);

  // ----- Effects: fetch Units daily + monthly -----
  useEffect(() => {
    const key = `${channel}|units|${unitsRange}|${normalizeIdsKey(appliedProductIds)}`;
    if (lastDailyUnitsKeyRef.current === key) return;
    lastDailyUnitsKeyRef.current = key;

    if (isChannel) {
      if (!channelParam) return;
      dispatch(fetchChannelProductUnits(channelParam, unitsRange, appliedSkusForChannel));
    } else {
      dispatch(fetchOrdersProductUnits(unitsRange, appliedUpcsForUS));
    }
  }, [
    dispatch,
    channel,
    isChannel,
    channelParam,
    unitsRange,
    appliedProductIds,
    normalizeIdsKey,
    appliedSkusForChannel,
    appliedUpcsForUS,
  ]);

  useEffect(() => {
    const key = `${channel}|units|${unitsYear}|${normalizeIdsKey(appliedProductIds)}`;
    if (lastMonthlyUnitsKeyRef.current === key) return;
    lastMonthlyUnitsKeyRef.current = key;

    if (!unitsYear) return;

    if (isChannel) {
      if (!channelParam) return;
      dispatch(fetchChannelProductUnitsByMonth(channelParam, unitsYear, appliedSkusForChannel));
    } else {
      dispatch(fetchOrdersProductUnitsByMonth(unitsYear, appliedUpcsForUS));
    }
  }, [
    dispatch,
    channel,
    isChannel,
    channelParam,
    unitsYear,
    appliedProductIds,
    normalizeIdsKey,
    appliedSkusForChannel,
    appliedUpcsForUS,
  ]);

  // ----- Prepare sales data -----
  const dailyRoot = dailyRootBound || {};
  const dailyData = dailyRoot.data || dailyRoot || {};
  const dailyCurrent = dailyData.current || {};
  const dailyLastYear = dailyData.lastYear || {};

  const dailySeriesRaw = Array.isArray(dailyCurrent.series) ? dailyCurrent.series : [];
  const dailySeriesNorm = dailySeriesRaw.map((p) => normalizeSeriesPoint(p, "day")).filter(Boolean);

  const dailyCategories = dailySeriesNorm.map((p) => p.label);
  const dailyValues = dailySeriesNorm.map((p) => p.revenue);

  const dailyTotal = safeNumber(dailyCurrent.total);
  const lastYearTotal = safeNumber(dailyLastYear.total);

  const rawPrev = dailyData.percentChangePrevious;
  const percentPrev =
    rawPrev === null || rawPrev === undefined ? null : Number.isFinite(Number(rawPrev)) ? Number(rawPrev) : null;

  const dailyPrevPositive = (percentPrev ?? 0) >= 0;

  const percentLastYear = Number.isFinite(dailyLastYear.percentChange)
    ? dailyLastYear.percentChange
    : lastYearTotal === 0
      ? null
      : ((dailyTotal - lastYearTotal) / Math.abs(lastYearTotal)) * 100;

  const dailyLastYearPositive = (percentLastYear ?? 0) >= 0;

  const monthlyRoot = monthlyRootBound || {};
  const monthlyData = monthlyRoot.data || monthlyRoot || {};
  const monthlyCurrent = monthlyData.current || {};
  const monthlyPrevYear = monthlyData.previous || {};

  const monthlySeriesRaw = Array.isArray(monthlyCurrent.series) ? monthlyCurrent.series : [];
  const monthlySeriesNorm = monthlySeriesRaw.map((p) => normalizeSeriesPoint(p, "month")).filter(Boolean);

  const monthlyCategories = monthlySeriesNorm.map((p) => p.label);
  const monthlyValues = monthlySeriesNorm.map((p) => p.revenue);

  const ytdCurrentTotal = safeNumber(monthlyCurrent.total);
  const ytdPreviousTotal = safeNumber(monthlyPrevYear.total);

  const rawPercentYear = monthlyData.percentChangeYear;
  let percentYear;
  if (Number.isFinite(rawPercentYear)) percentYear = rawPercentYear;
  else if (ytdPreviousTotal === 0 && ytdCurrentTotal > 0) percentYear = 100;
  else percentYear = 0;

  const yearPositive = percentYear >= 0;

  const displayYear = monthlyRoot.year || monthlyData.meta?.year || salesYear || currentYear;
  const previousYear = monthlyData.meta?.previousYear || Number(displayYear) - 1;

  // ----- Prepare units data -----
  const dailyUnitsRoot = dailyUnitsRootBound || {};
  const dailyUnitsData = dailyUnitsRoot.data || dailyUnitsRoot || {};
  const dailyUnitsCurrent = dailyUnitsData.current || {};

  const dailyUnitsBucketsRaw = Array.isArray(dailyUnitsCurrent.series) ? dailyUnitsCurrent.series : [];
  const dailyUnitsBuckets = dailyUnitsBucketsRaw.map((p) => normalizeUnitsPoint(p, "day")).filter(Boolean);

  const rawUnitsPrev = dailyUnitsData.percentChangePrevious;
  const percentUnitsPrev =
    rawUnitsPrev === null || rawUnitsPrev === undefined
      ? null
      : Number.isFinite(Number(rawUnitsPrev))
        ? Number(rawUnitsPrev)
        : null;

  const dailyUnitsPrevPositive = (percentUnitsPrev ?? 0) >= 0;

  const monthlyUnitsRoot = monthlyUnitsRootBound || {};
  const monthlyUnitsData = monthlyUnitsRoot.data || monthlyUnitsRoot || {};
  const monthlyUnitsCurrent = monthlyUnitsData.current || {};
  const monthlyUnitsPrevYear = monthlyUnitsData.previous || {};

  const monthlyUnitsBucketsRaw = Array.isArray(monthlyUnitsCurrent.series) ? monthlyUnitsCurrent.series : [];
  const monthlyUnitsBuckets = monthlyUnitsBucketsRaw.map((p) => normalizeUnitsPoint(p, "month")).filter(Boolean);

  const ytdUnitsPrevTotal = safeNumber(monthlyUnitsPrevYear.total);

  const rawPercentUnitsYear = monthlyUnitsData.percentChangeYear;
  let percentUnitsYear;
  if (Number.isFinite(rawPercentUnitsYear)) percentUnitsYear = rawPercentUnitsYear;
  else if (ytdUnitsPrevTotal === 0 && monthlyUnitsBuckets.length > 0) percentUnitsYear = 100;
  else percentUnitsYear = 0;

  const unitsYearPositive = percentUnitsYear >= 0;

  const dailyUnitsHasProducts = dailyUnitsBuckets.some((b) => Array.isArray(b.products) && b.products.length > 0);
  const monthlyUnitsHasProducts = monthlyUnitsBuckets.some((b) => Array.isArray(b.products) && b.products.length > 0);

  const dailyHasAmount = useMemo(() => {
    return (
      dailyUnitsBuckets.some((b) => safeNumber(b.amount) > 0) ||
      dailyUnitsBuckets.some((b) => (b.products || []).some((p) => safeNumber(p.amount) > 0))
    );
  }, [dailyUnitsBuckets]);

  const monthlyHasAmount = useMemo(() => {
    return (
      monthlyUnitsBuckets.some((b) => safeNumber(b.amount) > 0) ||
      monthlyUnitsBuckets.some((b) => (b.products || []).some((p) => safeNumber(p.amount) > 0))
    );
  }, [monthlyUnitsBuckets]);

  // console.log('Theme Colour', themeColor);
  
  // ----- Apex options -----
  const baseBarOpts = {
    chart: {
      toolbar: { show: false },
      fontFamily: "Chakra Petch, system-ui, -apple-system, BlinkMacSystemFont",
      background: "transparent",
    },
    plotOptions: {
      bar: { distributed: false, columnWidth: "55%", borderRadius: 2 },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: [],
      labels: { rotate: -15, style: { fontSize: "12px" } },
    },
    yaxis: {
      labels: {
        style: { fontSize: "12px" },
        formatter: (val) => formatNumberAbbrev(val),
      },
    },
    tooltip: { y: { formatter: (v) => formatCurrencyCompact(v) } },
    responsive: [
      {
        breakpoint: 992,
        options: {
          plotOptions: { bar: { columnWidth: "65%" } },
          xaxis: { labels: { rotate: -10, style: { fontSize: "11px" } } },
        },
      },
      {
        breakpoint: 576,
        options: {
          plotOptions: { bar: { columnWidth: "75%" } },
          xaxis: { labels: { rotate: 0, style: { fontSize: "10px" } } },
        },
      },
    ],
  };

  // Sales charts should ALWAYS be single themeColor
  const dailyOpts = {
    ...baseBarOpts,
    colors: [themeColor],
    xaxis: { ...baseBarOpts.xaxis, categories: dailyCategories },
    title: {
      text: `Product sales (${salesRangeCfg.label}) – ${channel}`,
      align: "center",
      style: { fontSize: "16px", fontWeight: 600, fontFamily: "Chakra Petch", color: secondColor },
    },
  };

  const dailySalesType = dailySalesChartType === "line" ? "line" : "bar";
  const dailySalesOpts =
    dailySalesType === "line"
      ? applyLineFixes(
          {
            ...dailyOpts,
            chart: { ...dailyOpts.chart, zoom: { enabled: false } },
          },
          [themeColor],
          isDarkUi
        )
      : applyLegendFixes(dailyOpts, [themeColor], isDarkUi);

    const monthlyOpts = {
      ...baseBarOpts,
      xaxis: { ...baseBarOpts.xaxis, categories: monthlyCategories },
      legend: {
        show: true,
        onItemClick: { toggleDataSeries: true },
        onItemHover: { highlightDataSeries: true },
      },
      title: {
        text: `Product sales – ${displayYear} – ${channel}`,
        align: "center",
        style: { fontSize: "16px", fontWeight: 600, fontFamily: "Chakra Petch", color: secondColor },
      },
    };

  const dailySeries = [{ name: "Daily Sales", data: dailyValues }];
  const monthlySeries = [{ name: "Monthly Sales", data: monthlyValues }];

  const monthlySalesType = monthlySalesChartType === "line" ? "line" : "bar";
  const monthlySalesOpts =
    monthlySalesType === "line"
      ? applyLineFixes(
          {
            ...monthlyOpts,
            chart: { ...monthlyOpts.chart, zoom: { enabled: false } },
          },
          [themeColor],
          isDarkUi
        )
      : applyLegendFixes(monthlyOpts, [themeColor], isDarkUi);

  const availableYears = useMemo(() => {
    const base = currentYear;
    return [base, base - 1, base - 2];
  }, [currentYear]);

  // Mark that we want to scroll AFTER rerender
  const markScrollToMonthlyUnits = useCallback(() => {
    pendingScrollToMonthlyUnitsRef.current = true;
  }, []);

  // Perform scroll only after the Monthly Units rerender (layout phase)
  useLayoutEffect(() => {
    if (!pendingScrollToMonthlyUnitsRef.current) return;
    if (monthlyUnitsLoading) return;

    pendingScrollToMonthlyUnitsRef.current = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        monthlyUnitsCardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
  }, [monthlyUnitsMetric, monthlyUnitsLoading]);

  const CONTROL_HEIGHT = 38;
  const CONTROL_RADIUS = 6;

  // ---------- CSV export helpers ----------

  const formatUSDate = (dateStr) => {
    if (!dateStr) return "";
    // Expecting "YYYY-MM-DD" (or "YYYY-MM-DDTHH:mm...")
    const base = String(dateStr).split(/[ T]/)[0];
    const [y, m, d] = base.split("-").map((x) => Number(x));
    if (![y, m, d].every(Number.isFinite)) return base;

    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${mm}/${dd}/${y}`;
  };

  const csvEscape = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    // wrap if contains comma/quote/newline
    if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const rowsToCsv = (rows) => rows.map((r) => r.map(csvEscape).join(",")).join("\n");

  const downloadCsv = (filename, csvText) => {
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  };

  // NEW: builds proper CSV rows with real columns
  // returns rows like: [Period, Product ID, Product Name, Units, Amount]
  const buildPerProductRows  = (buckets, { periodType = "day" } = {}) => {
    const list = Array.isArray(buckets) ? buckets : [];

    return list.flatMap((b) => {
      // DAILY: use "YYYY-MM-DD" (from raw.date) or label
      let period =
        periodType === "day"
          ? formatUSDate(b?.raw?.date) || b?.label || ""
          : b?.raw?.date || b?.label || "";


      // MONTHLY: prefer "Jan", "Feb" etc (your API provides label + year/month)
      if (periodType === "month") {
        if (b?.raw?.label && b?.raw?.year) period = `${b.raw.label} ${b.raw.year}`; // "Jan 2026"
        else if (b?.raw?.year && b?.raw?.month) period = `${b.raw.year}-${String(b.raw.month).padStart(2, "0")}`; // "2026-01"
        else period = b?.raw?.date || b?.label || "";
      }

      const products = Array.isArray(b.products) ? b.products : [];

      // keep empty periods visible in export
      if (!products.length) {
        return [[period, "", "", safeNumber(b.qty), safeNumber(b.amount)]];
      }


      return products.map((p) => [
        period,
        p?.productId ?? "",
        p?.productName ?? p?.name ?? "",
        safeNumber(p?.qty ?? p?.quantity ?? 0),
        safeNumber(p?.amount ?? p?.revenue ?? 0),
      ]);
    });
  };


  const exportDailyUnitsPerProductCsv = useCallback(() => {
    const periodLabel = "Date (MM/DD/YYYY)";
    const rows = buildPerProductRows(dailyUnitsBuckets, { periodType: "day" });

    const header = [
      "Channel",
      channel,
      "Units Range",
      unitsRangeCfg.label,
      "Products Filter",
      appliedProductIds?.length ? selectedProductsLabel : "All products",
    ];

    const csv = [
      rowsToCsv([header]),
      "",
      rowsToCsv([[periodLabel, "Product Id", "Product Name", "Units", "Amount"]]),
      rowsToCsv(rows),
    ].join("\n");

    const filename = `units_daily_per_product_${channel}_${normalizeRangeKey(unitsRange)}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    downloadCsv(filename, csv);
  }, [dailyUnitsBuckets, channel, unitsRange, unitsRangeCfg.label, appliedProductIds, selectedProductsLabel]);

  const exportMonthlyUnitsPerProductCsv = useCallback(() => {
    const rows = buildPerProductRows(monthlyUnitsBuckets, { periodType: "month" });

    const headerMeta = [
      ["Channel", channel],
      ["Year", String(unitsYear)],
      ["Products Filter", appliedProductIds?.length ? selectedProductsLabel : "All products"],
    ];

    const csv = [
      rowsToCsv(headerMeta),
      "",
      rowsToCsv([["Month", "Product ID", "Product Name", "Units", "Amount"]]),
      rowsToCsv(rows),
    ].join("\n");

    const filename = `units_monthly_per_product_${channel}_${unitsYear}_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(filename, csv);
  }, [monthlyUnitsBuckets, channel, unitsYear, appliedProductIds, selectedProductsLabel]);


  // ----- Render -----
  return (
    <div>
      {/* Header */}
      <div className="mb-3">
        <div className="row g-3 align-items-start align-items-xl-center">
          {/* Left */}
          <div className="col-12 col-xl-auto">
            <div>
              <ul className="breadcrumb">
                <h6>
                  <li className="breadcrumb-item active">PAGES / E-COMMERCE /</li>
                </h6>
                <h6>
                  <li className="breadcrumb-item">&nbsp;CHANNEL PRODUCT SALES</li>
                </h6>
              </ul>
              <h1 className="page-header mb-0">CHANNEL PRODUCT SALES</h1>
            </div>
          </div>

          {/* Center: Product multi-select dropdown */}
          <div className="col-12 col-xl">
            <div
              id="productsMultiSelectDropdown"
              className="mx-xl-auto position-relative"
              style={{
                maxWidth: 980,
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: 12,
                rowGap: 6,
                alignItems: "center",
              }}
            >
              <label className="mb-0" style={{ whiteSpace: "nowrap" }}>
                Products:
              </label>

              <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                <div className="dropdown flex-grow-1" style={{ minWidth: 0 }}>
                  <button
                    type="button"
                    className="form-control text-start d-flex align-items-center justify-content-between"
                    onClick={() => setProductsDropdownOpen((v) => !v)}
                    disabled={loading_analytics_product_options}
                    aria-expanded={productsDropdownOpen}
                    title="Select one or more products"
                    style={{
                      cursor: "pointer",
                      overflow: "hidden",
                      height: CONTROL_HEIGHT,
                      minHeight: CONTROL_HEIGHT,
                      borderRadius: CONTROL_RADIUS,
                    }}
                  >
                    <span className="text-truncate flex-grow-1" style={{ minWidth: 0 }}>
                      {loading_analytics_product_options ? "Loading…" : selectedProductsLabel}
                    </span>
                    <i className={`fa fa-chevron-${productsDropdownOpen ? "up" : "down"} ms-2 flex-shrink-0`} />
                  </button>

                  {productsDropdownOpen && (
                    <div
                      className="dropdown-menu show p-2"
                      style={{
                        width: "100%",
                        maxHeight: 280,
                        overflowY: "auto",
                        zIndex: 2000,
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between px-2 pb-2">
                        <div className="small text-muted">
                          {selectedProductIds.length ? `${selectedProductIds.length} selected` : "None selected"}
                        </div>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clearSelectedProducts();
                          }}
                          disabled={!selectedProductIds.length}
                        >
                          Clear
                        </button>
                      </div>

                      <div className="dropdown-divider my-1" />

                      {analytics_product_options_error ? (
                        <div className="text-danger small px-2 py-2">{analytics_product_options_error}</div>
                      ) : (analytics_product_options || []).length === 0 ? (
                        <div className="text-muted small px-2 py-2">No products found</div>
                      ) : (
                        (analytics_product_options || []).map((opt) => {
                          const id = String(opt.value);
                          const checked = selectedProductIds.includes(id);

                          return (
                            <div
                              key={id}
                              role="menuitemcheckbox"
                              aria-checked={checked}
                              className="dropdown-item d-flex align-items-center gap-2"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleProductId(id);
                              }}
                              style={{ cursor: "pointer", userSelect: "none" }}
                            >
                              <span
                                aria-hidden="true"
                                style={{
                                  width: 16,
                                  height: 16,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: 3,
                                  border: `1px solid ${checked ? themeColor : "rgba(255,255,255,0.35)"}`,
                                  background: checked ? themeColor : "transparent",
                                  boxShadow: checked ? `0 0 0 2px ${themeColor}33` : "none",
                                  flex: "0 0 auto",
                                }}
                              >
                                {checked ? (
                                  <i className="fa fa-check" style={{ color: "#fff", fontSize: 11, lineHeight: 1 }} />
                                ) : null}
                              </span>

                              <span className="flex-grow-1">{opt.label}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleProductsQueryClick}
                  disabled={!hasSelectionChanges}
                  title={hasSelectionChanges ? "Apply selected products" : "No changes to apply"}
                  style={{
                    height: CONTROL_HEIGHT,
                    width: 44,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    borderRadius: CONTROL_RADIUS,
                  }}
                >
                  <i className="fas fa-search" />
                </button>
              </div>

              <div />

              <div className="small" style={{ minHeight: 16 }}>
                {hasSelectionChanges ? <span className="text-warning">Selection changed — press Search to apply</span> : null}
              </div>

              {analytics_product_options_error ? (
                <>
                  <div />
                  <div className="text-danger small" style={{ whiteSpace: "nowrap" }}>
                    {analytics_product_options_error}
                  </div>
                </>
              ) : null}
            </div>
          </div>

          {/* Right */}
          <div className="col-12 col-xl-auto">
            <div className="d-flex align-items-center gap-2 justify-content-xl-end">
              <label className="mb-0 me-2" style={{ whiteSpace: "nowrap" }}>
                Sales Channel:
              </label>

              <select
                className="form-select w-auto"
                value={channel}
                onChange={(e) => {
                  setChannel(e.target.value);
                  setProductsDropdownOpen(false);
                }}
                style={{
                  height: CONTROL_HEIGHT,
                  minHeight: CONTROL_HEIGHT,
                  borderRadius: CONTROL_RADIUS,
                }}
              >
                <option value="US">US</option>
                {/* <option value="UK">UK</option> */}
                <option value="India">India</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Sales */}
      <Card className="mb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center px-4 pt-3">
          <div>
            <h4 className="mb-1">Daily Sales</h4>
            <div className="text-muted small">{salesRangeCfg.label}</div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="me-2 mb-0" style={{ fontWeight: 500 }}>
              Range:
            </span>
            <select className="form-select w-auto" value={salesRange} onChange={(e) => setSalesRange(e.target.value)}>
              {(channel === "US" ? rangeOptionsUS : rangeOptionsChannel).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <span className="ms-3 me-2 mb-0" style={{ fontWeight: 500 }}>
              Chart:
            </span>

            <select
              className="form-select w-auto"
              value={dailySalesChartType}
              onChange={(e) => setDailySalesChartType(normalizeChartType(e.target.value))}
            >
              <option value="bar">Bar</option>
              <option value="line">Line</option>
            </select>
          </div>
        </div>

        <div className="px-4 pt-3 pb-2">
          <div className="row text-center">
            <div className="col-md-4 mb-3">
              <div className="fw-bold" style={{ fontSize: 24, color: themeColor }}>
                {formatCurrencyCompact(dailyTotal)}
              </div>
              <div className="text-muted small">{salesRangeCfg.label}</div>
            </div>

            <div className="col-md-4 mb-3">
              <div
                className={`fw-bold ${percentPrev == null ? "text-muted" : dailyPrevPositive ? "text-success" : "text-danger"}`}
                style={{ fontSize: 18 }}
              >
                {percentPrev == null ? "—" : formatPercent(percentPrev)}{" "}
                {percentPrev == null ? null : <span style={{ fontSize: 14 }}>{dailyPrevPositive ? "↑" : "↓"}</span>}
              </div>
              <div className="text-muted small">{salesRangeCfg.prevLabel}</div>
            </div>

            <div className="col-md-4 mb-3">
              <div
                className={`fw-bold ${
                  percentLastYear == null ? "text-muted" : dailyLastYearPositive ? "text-success" : "text-danger"
                }`}
                style={{ fontSize: 18 }}
              >
                {percentLastYear == null ? "—" : formatPercent(percentLastYear)}{" "}
                {percentLastYear == null ? null : <span style={{ fontSize: 14 }}>{dailyLastYearPositive ? "↑" : "↓"}</span>}
              </div>
              <div className="text-muted small">Last year</div>
            </div>
          </div>
        </div>

        <div className="px-4 pb-4">
          {dailyLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : dailyError ? (
            <div className="text-danger text-center py-5">{dailyError}</div>
          ) : dailySeriesNorm.length === 0 ? (
            <div className="text-center py-5">No data for this range.</div>
          ) : (
            <Chart
              key={`daily-${chartKey}-${dailySalesType}`}
              options={dailySalesOpts}
              series={dailySeries}
              type={dailySalesType}
              height={280}
            />
          )}
        </div>
      </Card>

      {/* Monthly Sales */}
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center px-4 pt-3">
          <div>
            <h4 className="mb-1">Monthly Sales</h4>
            <div className="text-muted small">Year to date</div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="me-2 mb-0" style={{ fontWeight: 500 }}>
              Year:
            </span>
            <select className="form-select w-auto" value={salesYear} onChange={(e) => setSalesYear(Number(e.target.value))}>
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <span className="ms-3 me-2 mb-0" style={{ fontWeight: 500 }}>
              Chart:
            </span>

            <select
              className="form-select w-auto"
              value={monthlySalesChartType}
              onChange={(e) => setMonthlySalesChartType(normalizeChartType(e.target.value))}
            >
              <option value="bar">Bar</option>
              <option value="line">Line</option>
            </select>

          </div>
        </div>

        <div className="px-4 pt-3 pb-2">
          <div className="row text-center">
            <div className="col-md-4 mb-3">
              <div className="fw-bold" style={{ fontSize: 24, color: themeColor }}>
                {formatCurrencyCompact(ytdCurrentTotal)}
              </div>
              <div className="text-muted small">Year to date</div>
            </div>

            <div className="col-md-4 mb-3">
              <div className={`fw-bold ${yearPositive ? "text-success" : "text-danger"}`} style={{ fontSize: 18 }}>
                {formatPercent(percentYear)} <span style={{ fontSize: 14 }}>{yearPositive ? "↑" : "↓"}</span>
              </div>
              <div className="text-muted small">Last year</div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="fw-bold" style={{ fontSize: 16 }}>
                {previousYear}
              </div>
              <div className="text-muted small">Comparison year</div>
            </div>
          </div>
        </div>

        <div className="px-4 pb-4">
          {monthlyLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : monthlyError ? (
            <div className="text-danger text-center py-5">{monthlyError}</div>
          ) : monthlySeriesNorm.length === 0 ? (
            <div className="text-center py-5">No data for this year.</div>
          ) : (
            <Chart
              key={`monthly-${chartKey}-${monthlySalesType}`}
              options={monthlySalesOpts}
              series={monthlySeries}
              type={monthlySalesType}
              height={280}
            />
          )}
        </div>
      </Card>

      {/* Daily Units (Qty + Amount side-by-side) */}
      <Card className="mb-4 mt-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center px-4 pt-3">
          <div>
            <h4 className="mb-1">
              {(() => {
                const showAmount = channel === "US" || dailyHasAmount;
                if (!showAmount) return dailyUnitsHasProducts ? "Daily Units per Product" : "Daily Units";
                return dailyUnitsHasProducts ? "Daily Units & Amount per Product" : "Daily Units & Amount";
              })()}
            </h4>
            <div className="text-muted small">{unitsRangeCfg.label}</div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="ms-2 me-2 mb-0" style={{ fontWeight: 500 }}>
              Range:
            </span>

            <select className="form-select w-auto" value={unitsRange} onChange={(e) => setUnitsRange(e.target.value)}>
              {UNITS_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <span className="ms-3 me-2 mb-0" style={{ fontWeight: 500 }}>
              Chart:
            </span>

            <select
              className="form-select w-auto"
              value={dailyUnitsChartType}
              onChange={(e) => setDailyUnitsChartType(normalizeChartType(e.target.value))}
            >
              <option value="bar">Bar</option>
              <option value="line">Line</option>
            </select>

            <button
              type="button"
              className="btn btn-outline-secondary ms-2"
              onClick={exportDailyUnitsPerProductCsv}
              disabled={dailyUnitsLoading || !dailyUnitsBuckets?.length}
              title="Export Daily Units & Amount per Product (CSV)"
              style={{ height: CONTROL_HEIGHT, borderRadius: CONTROL_RADIUS }}
            >
              <i className="fa fa-download me-2" />
              Export
            </button>
          </div>
        </div>

        <div className="px-4 pb-4">
          {dailyUnitsLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : dailyUnitsError ? (
            <div className="text-danger text-center py-5">{dailyUnitsError}</div>
          ) : dailyUnitsBuckets.length === 0 ? (
            <div className="text-center py-5">No data for this range.</div>
          ) : (
            (() => {
              const showAmount = channel === "US" || dailyHasAmount;
              const categories = dailyUnitsBuckets.map((b) => b.label);

              const qtySeries = dailyUnitsHasProducts
                ? buildPerProductMetricSeries(dailyUnitsBuckets, "qty")
                : [{ name: "Daily Units", data: dailyUnitsBuckets.map((b) => safeNumber(b.qty)) }];

              const amountSeries = dailyUnitsHasProducts
                ? buildPerProductMetricSeries(dailyUnitsBuckets, "amount")
                : [{ name: "Daily Amount", data: dailyUnitsBuckets.map((b) => safeNumber(b.amount)) }];

                
              const unitsType = dailyUnitsChartType === "line" ? "line" : "bar";

              const useFixedGreys = dailyUnitsHasProducts && isSpecialTheme(themeColor);

              const qtyColors = dailyUnitsHasProducts
                ? (useFixedGreys ? fixed4Greys(qtySeries.length) : makeProductPalette(themeColor, qtySeries.length, isDarkUi))
                : unitsType === "line"
                  ? [themeColor]
                  : makeThemePalette(themeColor, categories.length || 1);

              const amountColors = dailyUnitsHasProducts
                ? (useFixedGreys ? fixed4Greys(amountSeries.length) : makeProductPalette(themeColor, amountSeries.length, isDarkUi))
                : unitsType === "line"
                  ? [themeColor]
                  : makeThemePalette(themeColor, categories.length || 1);


              const commonUnitsOpts = {
                chart: { ...baseBarOpts.chart, stacked: false },
                plotOptions: {
                  ...baseBarOpts.plotOptions,
                  bar: {
                    ...baseBarOpts.plotOptions?.bar,
                    distributed: !dailyUnitsHasProducts && unitsType !== "line",
                    columnWidth: "55%",
                  },
                },
                dataLabels: baseBarOpts.dataLabels,
                responsive: baseBarOpts.responsive,
                legend: {
                  show: !!dailyUnitsHasProducts,
                  position: "bottom",
                  fontFamily: "Chakra Petch, system-ui, -apple-system, BlinkMacSystemFont",
                },
                xaxis: { ...baseBarOpts.xaxis, categories },
              };

              const qtyOpts = {
                ...baseBarOpts,
                ...commonUnitsOpts,
                colors: qtyColors,
                yaxis: {
                  ...baseBarOpts.yaxis,
                  labels: { ...baseBarOpts.yaxis.labels, formatter: (v) => formatNumberAbbrev(v) },
                },
                tooltip: { y: { formatter: (v) => formatNumberAbbrev(v) } },
                title: {
                  text: `Product Units (${unitsRangeCfg.label}) – ${channel}`,
                  align: "center",
                  style: { fontSize: "16px", fontWeight: 600, fontFamily: "Chakra Petch", color: secondColor },
                },
              };

              const amountOpts = {
                ...baseBarOpts,
                ...commonUnitsOpts,
                colors: amountColors,
                yaxis: {
                  ...baseBarOpts.yaxis,
                  labels: { ...baseBarOpts.yaxis.labels, formatter: (v) => formatCurrencyCompact(v) },
                },
                tooltip: { y: { formatter: (v) => formatCurrencyCompact(v) } },
                title: {
                  text: `Product Amount (${unitsRangeCfg.label}) – ${channel}`,
                  align: "center",
                  style: { fontSize: "16px", fontWeight: 600, fontFamily: "Chakra Petch", color: secondColor },
                },
              };

            const legendEvents = dailyUnitsHasProducts ? makeLegendToggleEvents() : undefined;
            
            const qtyFinalOpts =
              unitsType === "line"
                ? applyLineFixes(qtyOpts, qtyColors, isDarkUi, legendEvents)
                : applyLegendFixes(qtyOpts, qtyColors, isDarkUi, legendEvents);

            const amountFinalOpts =
              unitsType === "line"
                ? applyLineFixes(amountOpts, amountColors, isDarkUi, legendEvents)
                : applyLegendFixes(amountOpts, amountColors, isDarkUi, legendEvents);


              return (
                <div className="row g-3">
                  <div className="col-12 col-lg-6">
                    <Chart
                      key={`daily-units-${chartKey}-qty-${unitsType}`}
                      options={qtyFinalOpts}
                      series={qtySeries}
                      type={unitsType}
                      height={280}
                    />
                  </div>

                  <div className="col-12 col-lg-6">
                    {!showAmount ? (
                      <div className="text-center py-5 text-muted">Amount isn’t available for this channel/range.</div>
                    ) : (
                      <Chart
                        key={`daily-units-${chartKey}-amount-${unitsType}`}
                        options={amountFinalOpts}
                        series={amountSeries}
                        type={unitsType}
                        height={280}
                      />
                    )}
                  </div>
                </div>
              );
            })()
          )}
        </div>
      </Card>

      {/* Monthly Units (Qty + Amount side-by-side) */}
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center px-4 pt-3">
          <div>
            <h4 className="mb-1">
              {(() => {
                const showAmount = channel === "US" || monthlyHasAmount;
                if (!showAmount) return monthlyUnitsHasProducts ? "Monthly Units per Product" : "Monthly Units";
                return monthlyUnitsHasProducts ? "Monthly Units & Amount per Product" : "Monthly Units & Amount";
              })()}
            </h4>
            <div className="text-muted small">Year to date</div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="ms-2 me-2 mb-0" style={{ fontWeight: 500 }}>
              Year:
            </span>
            <select className="form-select w-auto" value={unitsYear} onChange={(e) => setUnitsYear(Number(e.target.value))}>
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <span className="ms-3 me-2 mb-0" style={{ fontWeight: 500 }}>
              Chart:
            </span>

            <select
              className="form-select w-auto"
              value={monthlyUnitsChartType}
              onChange={(e) => setMonthlyUnitsChartType(normalizeChartType(e.target.value))}
            >
              <option value="bar">Bar</option>
              <option value="line">Line</option>
            </select>

            <button
              type="button"
              className="btn btn-outline-secondary ms-2"
              onClick={exportMonthlyUnitsPerProductCsv}
              disabled={monthlyUnitsLoading || !monthlyUnitsBuckets?.length}
              title="Export Monthly Units & Amount per Product (CSV)"
              style={{ height: CONTROL_HEIGHT, borderRadius: CONTROL_RADIUS }}
            >
              <i className="fa fa-download me-2" />
              Export
            </button>
          </div>
        </div>

        <div className="px-4 pb-4">
          {monthlyUnitsLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : monthlyUnitsError ? (
            <div className="text-danger text-center py-5">{monthlyUnitsError}</div>
          ) : monthlyUnitsBuckets.length === 0 ? (
            <div className="text-center py-5">No data for this year.</div>
          ) : (
            (() => {
              const showAmount = channel === "US" || monthlyHasAmount;
              const categories = monthlyUnitsBuckets.map((b) => b.label);

              const qtySeries = monthlyUnitsHasProducts
                ? buildPerProductMetricSeries(monthlyUnitsBuckets, "qty")
                : [{ name: "Monthly Units", data: monthlyUnitsBuckets.map((b) => safeNumber(b.qty)) }];

              const amountSeries = monthlyUnitsHasProducts
                ? buildPerProductMetricSeries(monthlyUnitsBuckets, "amount")
                : [{ name: "Monthly Amount", data: monthlyUnitsBuckets.map((b) => safeNumber(b.amount)) }];

              const unitsType = monthlyUnitsChartType === "line" ? "line" : "bar";

              // product palettes: PASS isDarkUi so neutral theme colors never go black on dark cover
              const useFixedGreys = monthlyUnitsHasProducts && isSpecialTheme(themeColor);

              const qtyColors = monthlyUnitsHasProducts
                ? (useFixedGreys ? fixed4Greys(qtySeries.length) : makeProductPalette(themeColor, qtySeries.length, isDarkUi))
                : unitsType === "line"
                  ? [themeColor]
                  : makeThemePalette(themeColor, categories.length || 1);

              const amountColors = monthlyUnitsHasProducts
                ? (useFixedGreys ? fixed4Greys(amountSeries.length) : makeProductPalette(themeColor, amountSeries.length, isDarkUi))
                : unitsType === "line"
                  ? [themeColor]
                  : makeThemePalette(themeColor, categories.length || 1);


              const commonUnitsOpts = {
                chart: { ...baseBarOpts.chart, stacked: false },
                plotOptions: {
                  ...baseBarOpts.plotOptions,
                  bar: {
                    ...baseBarOpts.plotOptions?.bar,
                    distributed: !monthlyUnitsHasProducts && unitsType !== "line",
                    columnWidth: "55%",
                  },
                },
                dataLabels: baseBarOpts.dataLabels,
                responsive: baseBarOpts.responsive,
                legend: {
                  show: !!monthlyUnitsHasProducts,
                  position: "bottom",
                  fontFamily: "Chakra Petch, system-ui, -apple-system, BlinkMacSystemFont",
                },
                xaxis: { ...baseBarOpts.xaxis, categories },
              };

              const qtyOpts = {
                ...baseBarOpts,
                ...commonUnitsOpts,
                colors: qtyColors,
                yaxis: {
                  ...baseBarOpts.yaxis,
                  labels: { ...baseBarOpts.yaxis.labels, formatter: (v) => formatNumberAbbrev(v) },
                },
                tooltip: { y: { formatter: (v) => formatNumberAbbrev(v) } },
                title: {
                  text: `Product Units – ${displayYear} – ${channel}`,
                  align: "center",
                  style: { fontSize: "16px", fontWeight: 600, fontFamily: "Chakra Petch", color: secondColor },
                },
              };

              const amountOpts = {
                ...baseBarOpts,
                ...commonUnitsOpts,
                colors: amountColors,
                yaxis: {
                  ...baseBarOpts.yaxis,
                  labels: { ...baseBarOpts.yaxis.labels, formatter: (v) => formatCurrencyCompact(v) },
                },
                tooltip: { y: { formatter: (v) => formatCurrencyCompact(v) } },
                title: {
                  text: `Product Amount – ${displayYear} – ${channel}`,
                  align: "center",
                  style: { fontSize: "16px", fontWeight: 600, fontFamily: "Chakra Petch", color: secondColor },
                },
              };

              const legendEvents = monthlyUnitsHasProducts ? makeLegendToggleEvents() : undefined;
          
              const qtyFinalOpts =
                unitsType === "line"
                  ? applyLineFixes(qtyOpts, qtyColors, isDarkUi, legendEvents)
                  : applyLegendFixes(qtyOpts, qtyColors, isDarkUi, legendEvents);

              const amountFinalOpts =
                unitsType === "line"
                  ? applyLineFixes(amountOpts, amountColors, isDarkUi, legendEvents)
                  : applyLegendFixes(amountOpts, amountColors, isDarkUi, legendEvents);

              return (
                <div className="row g-3">
                  <div className="col-12 col-lg-6">
                    <Chart
                      key={`monthly-units-${chartKey}-qty-${unitsType}`}
                      options={qtyFinalOpts}
                      series={qtySeries}
                      type={unitsType}
                      height={280}
                    />
                  </div>

                  <div className="col-12 col-lg-6">
                    {!showAmount ? (
                      <div className="text-center py-5 text-muted">Amount isn’t available for this channel/year.</div>
                    ) : (
                      <Chart
                        key={`monthly-units-${chartKey}-amount-${unitsType}`}
                        options={amountFinalOpts}
                        series={amountSeries}
                        type={unitsType}
                        height={280}
                      />
                    )}
                  </div>
                </div>
              );
            })()
          )}
        </div>

        {/* anchor ref here (stable DOM node) */}
        <div ref={monthlyUnitsCardRef} style={{ height: 1 }} />
      </Card>
    </div>
  );
};

export default ProductSales;